import React, { Suspense, useEffect } from 'react';
import { useQuery } from "react-query";
import * as apiClient from "../api-client";
import '../index.css';
import Loader from '../components/LoaderComponent';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import FAQAllInclusive from '../components/HolidayFAQs/AllInclusiveFaq';
import BlogSliderType from '../components/Blogs/BlogSliderType';
import HolidayTypeHotelsList from '../components/HolidayTypeHotelList';
import Slider1 from "../components/CountrySliders/Maldives/AllInclusiveSlider";
import Slider2 from "../components/CountrySliders/Mauritius/AllInclusiveSlider";
import HotelSliderSkeleton from '../components/Skeletons/HotelSliderSkeleton';
import ReadMoreLines from '../components/ReadMoreLines';

const LazyWhyChoose = React.lazy(() => import('../components/WhyChoose/AllInclusive'));
const LazyVacationTypes = React.lazy(() => import('../components/Home/VacationTypes'));

const PageAllInclusive = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { data: hotels } = useQuery("fetchAllInclusiveHotels", apiClient.fetchHotels);

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>All Inclusive Holidays on a Budget | Halo Holidays</title>
          <meta name="description" content="Escape to paradise with all-inclusive holidays. Explore stunning destinations, indulge in luxury amenities, and enjoy a stress-free getaway with everything included." />
        </Helmet>
      </HelmetProvider>

      <section className="max-h-640 md:min-h-0 lg:min-h-0 my-8">
        <hr className="mb-10" />
        <h2 className="text-2xl font-semibold items-center justify-center flex mt-10">More reasons to choose us</h2>
        <Suspense fallback={<Loader />}>
          <LazyWhyChoose />
        </Suspense>
        <hr className="mb-10 mt-10" />
      </section>

      <section className="mx-4 my-4 md:mx-0 md:my-0">

        <div className='mt-2 mb-4'>
          <h1 className="text-2xl font-semibold">All Inclusive Holidays</h1>
          <ReadMoreLines mobileMaxRows={2} desktopMaxRows={2}>
            <p className="text-black md:mb-1 md:mt-2">
              If you're dreaming of a holiday where relaxation is the top priority and you don't want to constantly worry about your spending, then all-inclusive holidays are the perfect solution for you. These packages offer incredible value for money, bundling your flights, accommodation, meals, drinks, and often a wide range of activities into one upfront price.
              <br /><br />
              Families especially love the convenience of all-inclusive holidays. With kids' clubs, water parks, and endless entertainment options, parents can relax knowing their little ones are having a blast while they enjoy some well-deserved downtime by the pool or at the spa. Couples seeking a romantic getaway also appreciate the all-inclusive model, as it allows them to indulge in gourmet dining, couples' massages, and sunset cruises without constantly reaching for their wallets.
              <br /><br />
              But it's not just about convenience and cost savings. All-inclusive resorts are known for their impressive amenities and activities, ensuring there's never a dull moment. Whether you're craving a culinary adventure with multiple restaurants serving diverse cuisines, seeking thrills at a water park or on a snorkeling excursion, or simply want to unwind by the pool with a cocktail in hand, all-inclusive resorts cater to a wide range of interests.
              <br /><br />
              The best part? You can often find fantastic all-inclusive last minute deals that offer even more value for your money. From early booking discounts to last-minute specials, there are plenty of opportunities to snag a great deal on your dream vacation. So why not let Halo Holidays take the stress out of planning your next getaway? We offer a wide selection of all-inclusive holidays to some of the world's most beautiful destinations, ensuring you'll find the perfect escape to suit your needs and budget.
            </p>
            <br />
            <h2 className='text-xl font-semibold'>Explore Our All Inclusive Resorts</h2>
            <p className='text-black'>
              Indulge in the ultimate worry-free getaway with all-inclusive resorts, where luxury and convenience meet to create a truly unforgettable holiday experience. From pristine beaches and world-class dining to endless activities and top-shelf drinks, these havens offer everything you need for a relaxing and rejuvenating escape. Whether you're seeking a romantic retreat for two, a fun-filled family adventure, or a solo escape to recharge your batteries, all-inclusive resorts cater to every type of traveller.
              <br /><br />
              With packages that include your flights, accommodation, meals, drinks, and even exciting excursions, you can leave your wallet behind and simply focus on enjoying every moment of your holiday. From the Caribbean's turquoise waters to the Mediterranean's ancient charm and the exotic allure of a holiday to Maldives, the world is your oyster when it comes to choosing your perfect all-inclusive resort.
            </p>
            <br />
            <h2 className='text-xl font-semibold'>Not into All-Inclusive? Find Your Perfect Holiday Here</h2>
            <p className='text-black'>
              Not into the all-inclusive scene? No problem! At Halo Holidays, we understand that everyone has their own unique travel style. That's why we offer a wide range of options beyond all-inclusive, tailored to your individual preferences and budget. Whether you're dreaming of a luxurious villa in Bali, a cultural immersion tour in Japan, a thrilling safari adventure in Africa, or a last-minute city break in Europe, we have the perfect holiday for you.
              <br /><br />
              Explore our diverse collection of destinations and experiences, from family-friendly resorts to romantic getaways and solo adventures. Let us help you create a bespoke itinerary that matches your dreams and desires, so you can experience the holiday of a lifetime.
            </p>
          </ReadMoreLines>
        </div>

        <Suspense fallback={<Loader />}>
          <section>
            <div className='mt-6 -mb-2 md:mt-0 md:mb-2'>
              <h2 className="text-xl font-semibold">Maldives Holiday Deals</h2>
            </div>
            <div className="hidden md:block lg:block lgx2:block">
              {hotels && (
                <HolidayTypeHotelsList hotels={hotels} holidayType="All Inclusive" holidayCountry="Maldives" />
              )}
            </div>
            <Suspense fallback={<HotelSliderSkeleton />}>
              <div className="container md:hidden lg:hidden">
                <Slider1 hotels={hotels ?? []} />
              </div>
            </Suspense>
          </section>
        </Suspense>

        <Suspense fallback={<Loader />}>
          <section>
            <div className='md:mt-4 -mb-2 md:mb-2'>
              <h2 className="text-xl font-semibold">Mauritius Holiday Deals</h2>
            </div>
            <div className="hidden md:block lg:block lgx2:block">
              {hotels && (
                <HolidayTypeHotelsList hotels={hotels} holidayType="All Inclusive" holidayCountry="Mauritius" />
              )}
            </div>
            <Suspense fallback={<HotelSliderSkeleton />}>
              <div className="container md:hidden lg:hidden">
                <Slider2 hotels={hotels ?? []} />
              </div>
            </Suspense>
          </section>
        </Suspense>

        <FAQAllInclusive />

        <Suspense fallback={<div>Loading Holiday Types...</div>}>
          <div className='p-4 md:p-0 md:mt-12 md:mb-12'>
            <LazyVacationTypes />
          </div>
        </Suspense>

        <section className='p-4 md:p-0'>
          <h2 className='text-2xl font-semibold mb-2'>Related Blogs</h2>
          <BlogSliderType blogType='all_inclusive' />
        </section>

      </section>
    </>
  );
};

export default PageAllInclusive;
